import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { 
  Search, 
  Library, 
  Eye, 
  Calendar, 
  User, 
  Building,
  Play,
  FileText,
  Grid3X3,
  List,
  Image,
  SlidersHorizontal,
  ArrowUpDown
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PresentationLibrary() {
  const [presentations, setPresentations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("grid"); // grid or list
  const [sortBy, setSortBy] = useState("newest"); // newest, oldest, most_viewed
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [departments, setDepartments] = useState([]);
  
  const location = useLocation();
  const isAdminView = location.pathname.startsWith("/admin");

  useEffect(() => {
    fetchPresentations();
    fetchDepartments();
  }, []);

  const fetchPresentations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/library/presentations`);
      setPresentations(response.data.presentations || []);
    } catch (error) {
      console.error("Failed to fetch presentations:", error);
      toast.error("Failed to load library");
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/analytics/reports`);
      setDepartments(response.data.departments || []);
    } catch (error) {
      console.error("Failed to fetch departments:", error);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/library/presentations?search=${encodeURIComponent(searchQuery)}`);
      setPresentations(response.data.presentations || []);
    } catch (error) {
      console.error("Search failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const trackView = async (sessionId) => {
    try {
      await axios.post(`${API}/library/presentations/${sessionId}/view`);
    } catch (error) {
      console.error("Failed to track view:", error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Filter by search query and department
  let filteredPresentations = presentations;
  
  if (searchQuery) {
    filteredPresentations = filteredPresentations.filter(p =>
      p.topic_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.concept_overview?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }
  
  if (selectedDepartment !== "all") {
    filteredPresentations = filteredPresentations.filter(p => p.department === selectedDepartment);
  }

  // Sort presentations
  const sortedPresentations = [...filteredPresentations].sort((a, b) => {
    if (sortBy === "newest") {
      return new Date(b.created_at) - new Date(a.created_at);
    } else if (sortBy === "oldest") {
      return new Date(a.created_at) - new Date(b.created_at);
    } else if (sortBy === "most_viewed") {
      return (b.view_count || 0) - (a.view_count || 0);
    }
    return 0;
  });

  // Group by month
  const groupedByMonth = sortedPresentations.reduce((acc, p) => {
    const month = p.month || "Unknown";
    if (!acc[month]) acc[month] = [];
    acc[month].push(p);
    return acc;
  }, {});

  const sortedMonths = Object.keys(groupedByMonth).sort((a, b) => b.localeCompare(a));

  return (
    <div className="p-6 space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Manrope' }}>
            <Library className="inline w-7 h-7 mr-2 text-primary" />
            Presentation Library
          </h1>
          <p className="text-[#5C5C5C] mt-1">
            Browse and learn from {presentations.length} presentations across the organization
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === "grid" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("grid")}
            className="rounded-full"
          >
            <Grid3X3 className="w-4 h-4" />
          </Button>
          <Button
            variant={viewMode === "list" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("list")}
            className="rounded-full"
          >
            <List className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Filters Row */}
      <div className="flex flex-col md:flex-row gap-4">
        {/* Search Bar */}
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
          <Input
            placeholder="Search by name, topic, or presenter..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className="pl-12 py-5 rounded-full border-2 focus:border-primary"
          />
        </div>
        
        {/* Department Filter */}
        <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
          <SelectTrigger className="w-[180px] rounded-full">
            <Building className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Department" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Departments</SelectItem>
            {departments.map(dept => (
              <SelectItem key={dept} value={dept}>{dept}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {/* Sort */}
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[160px] rounded-full">
            <ArrowUpDown className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest First</SelectItem>
            <SelectItem value="oldest">Oldest First</SelectItem>
            <SelectItem value="most_viewed">Most Viewed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : sortedPresentations.length === 0 ? (
        <div className="text-center py-20">
          <Library className="w-16 h-16 text-[#9CA3AF] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-[#1A1A1A]">No presentations found</h3>
          <p className="text-[#5C5C5C] mt-2">
            {searchQuery ? "Try a different search term" : "Presentations will appear here once completed"}
          </p>
        </div>
      ) : (
        /* Gallery by Month */
        <div className="space-y-8">
          {sortedMonths.map(month => (
            <div key={month}>
              <div className="flex items-center gap-3 mb-4">
                <Calendar className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-[#1A1A1A]">
                  {new Date(month + "-01").toLocaleDateString([], { month: 'long', year: 'numeric' })}
                </h2>
                <span className="text-sm text-[#5C5C5C] bg-gray-100 px-2 py-0.5 rounded-full">
                  {groupedByMonth[month].length} presentations
                </span>
              </div>
              
              {viewMode === "grid" ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {groupedByMonth[month].map(presentation => (
                    <Link
                      key={presentation.id}
                      to={isAdminView ? `/admin/library/${presentation.id}` : `/library/${presentation.id}`}
                      onClick={() => trackView(presentation.id)}
                    >
                      <Card className="group overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer border-2 hover:border-primary/30">
                        {/* Thumbnail */}
                        <div className="aspect-video bg-gradient-to-br from-[#F3F4F6] to-[#E5E7EB] relative overflow-hidden">
                          {presentation.first_slide_url ? (
                            <img
                              src={`${process.env.REACT_APP_BACKEND_URL}${presentation.first_slide_url}`}
                              alt={presentation.topic_name}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            className={`absolute inset-0 ${presentation.first_slide_url ? 'hidden' : 'flex'} items-center justify-center`}
                          >
                            <Image className="w-12 h-12 text-[#9CA3AF]" />
                          </div>
                          
                          {/* Play overlay */}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                            <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity transform scale-75 group-hover:scale-100">
                              <Play className="w-6 h-6 text-primary ml-1" />
                            </div>
                          </div>
                          
                          {/* View count badge */}
                          <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {presentation.view_count || 0}
                          </div>
                          
                          {/* Slide count badge */}
                          {presentation.slide_count > 0 && (
                            <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                              <FileText className="w-3 h-3" />
                              {presentation.slide_count} slides
                            </div>
                          )}
                        </div>
                        
                        {/* Info */}
                        <CardContent className="p-4">
                          <h3 className="font-semibold text-[#1A1A1A] line-clamp-1 group-hover:text-primary transition-colors">
                            {presentation.topic_name}
                          </h3>
                          <p className="text-sm text-[#5C5C5C] line-clamp-2 mt-1 h-10">
                            {presentation.concept_overview}
                          </p>
                          <div className="flex items-center gap-2 mt-3 text-xs text-[#9CA3AF]">
                            <User className="w-3 h-3" />
                            <span className="truncate">{presentation.user_name}</span>
                            {presentation.department && (
                              <>
                                <span>•</span>
                                <Building className="w-3 h-3" />
                                <span className="truncate">{presentation.department}</span>
                              </>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              ) : (
                /* List View */
                <div className="space-y-3">
                  {groupedByMonth[month].map(presentation => (
                    <Link
                      key={presentation.id}
                      to={isAdminView ? `/admin/library/${presentation.id}` : `/library/${presentation.id}`}
                      onClick={() => trackView(presentation.id)}
                    >
                      <Card className="group hover:shadow-lg transition-all cursor-pointer border hover:border-primary/30">
                        <CardContent className="p-4 flex items-center gap-4">
                          {/* Thumbnail */}
                          <div className="w-32 h-20 bg-[#F3F4F6] rounded-lg overflow-hidden flex-shrink-0">
                            {presentation.first_slide_url ? (
                              <img
                                src={`${process.env.REACT_APP_BACKEND_URL}${presentation.first_slide_url}`}
                                alt={presentation.topic_name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Image className="w-8 h-8 text-[#9CA3AF]" />
                              </div>
                            )}
                          </div>
                          
                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-[#1A1A1A] group-hover:text-primary transition-colors">
                              {presentation.topic_name}
                            </h3>
                            <p className="text-sm text-[#5C5C5C] line-clamp-1 mt-1">
                              {presentation.concept_overview}
                            </p>
                            <div className="flex items-center gap-3 mt-2 text-xs text-[#9CA3AF]">
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {presentation.user_name}
                              </span>
                              <span className="flex items-center gap-1">
                                <Building className="w-3 h-3" />
                                {presentation.department || 'N/A'}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatDate(presentation.created_at)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Eye className="w-3 h-3" />
                                {presentation.view_count || 0} views
                              </span>
                            </div>
                          </div>
                          
                          {/* Arrow */}
                          <Play className="w-5 h-5 text-[#9CA3AF] group-hover:text-primary transition-colors" />
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
